import { IFileDocument } from '../models/shared.model';
import { ActionHelper } from './action.helper';
import { MatDialog } from '@angular/material/dialog';

export class UploadHelper {
    private static supportedFileNameEndings = [
        '.pdf', '.jpg', '.jpeg', '.bmp', '.tif', '.tiff', '.heic', '.png', '.gif',
        '.txt', '.csv', '.log', '.docx', '.xlsx', '.pptx', '.dwg', '.mp4', '.m4p',
        '.m4v', '.mpg', '.mp2', '.mpeg', '.mpe', '.mpv', '.webm', '.msg'
    ];

    static getGeneralAcceptAttr() {
        return this.supportedFileNameEndings.join(',');
    }

    static isSupportedGeneralFileType(file: File) {
        return this.supportedFileNameEndings.some(x => file?.name.toLowerCase().endsWith(x));
    }

    static showFileTypeErrorIfNecessary(files: File|File[], dialog: MatDialog) {
        let fileNames = '';

        if (!Array.isArray(files)) {
            files = [files];
        }

        for (const file of files) {
            if (!this.isSupportedGeneralFileType(file)) {
                if (!fileNames) {
                    fileNames += file?.name;
                } else {
                    fileNames += `, ${file?.name}`;
                }
            }
        }

        if (!fileNames) {
            return;
        }

        ActionHelper.showAction('ERROR', dialog, {
            replaceKeys: true,
            errorSnippets: {
                title: '%UNSUPPORTED_FILE_TYPES%',
                description: `%THE_FOLLOWING_FILES_ARE_UNSUPPORTED%: ${fileNames}`
            }
        })
    }

    /**
     * Validates and returns Promise which resolves file as base64 string
     */
    static uploadFile(file: File, regExp?: RegExp): Promise<IFileDocument> {
        const mimeType = file.type;

        if (regExp && mimeType.match(regExp) === null) {
            throw new Error('Incorrect type.');
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        return new Promise<IFileDocument>((resolve => reader.onload = () => {
            resolve({
                Body: (reader.result as string).split(';base64,')[1],
                Name: file.name,
                MimeType: mimeType,
                Filedate: new Date().toISOString()
            });
        }));
    }

    static getDataUrlFromDocument(document: IFileDocument): string {
        if (document) {
            return 'data: ' + document.MimeType + ' ;base64,' + document.Body;
        }

        return '';
    }

    static getIconUrlFromMimeType(mimeType: string, fileUrl?: string): string {
        if ([
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.openxmlformats-officedocument.wordprocessingml',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
            'application/vnd.ms-word.document.macroEnabled.12',
            'application/vnd.ms-word.template.macroEnabled.12',
            'application/vnd.oasis.opendocument.text',
        ].includes(mimeType)) {
            return '/assets/icons/ms-word.svg';
        }

        if ([
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
            'application/vnd.ms-excel.sheet.macroEnabled.12',
            'application/vnd.ms-excel.template.macroEnabled.12',
            'application/vnd.ms-excel.addin.macroEnabled.12',
            'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
            'application/vnd.oasis.opendocument.spreadsheet',
        ].includes(mimeType)) {
            return '/assets/icons/ms-excel.svg';
        }

        if ([
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'application/vnd.openxmlformats-officedocument.presentationml.template',
          'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
          'application/vnd.ms-powerpoint.addin.macroEnabled.12',
          'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
          'application/vnd.ms-powerpoint.template.macroEnabled.12',
          'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
          'application/vnd.oasis.opendocument.presentation',
        ].includes(mimeType)) {
          return '/assets/icons/ms-power-point.svg';
        }


        if (['application/pdf'].includes(mimeType)) {
            return '/assets/icons/pdf.svg';
        }

        if (['image/png', 'image/svg', 'image/jpeg', 'image/bmp', 'image/jpg', 'image/svg+xml'].includes(mimeType)) {
            return fileUrl || '/assets/icons/image-file.svg';
        }

        return '/assets/icons/attachment.svg';
    }
}
